import { useCallback, useEffect, useRef } from 'react';
import { useAppStore, recalculateStore } from '@/store/useAppStore';
import { ProfileSwitcher } from './ProfileSwitcher';
import { LockMenu } from './AppLock';
import { useAppLock } from './AppLock';
import { CapIcon } from './ui/Icons';
import { DEMO_DATASETS, hydrateDemoDataset } from '@/data/demoDatasets';
import { useTheme } from '@/hooks/useTheme';

export function Header() {
  const toggleSettings = useAppStore((s) => s.toggleSettings);
  const settingsOpen = useAppStore((s) => s.settingsOpen);
  const showLoadDemo = useAppStore((s) => s.showLoadDemo);
  const demoPickerOpen = useAppStore((s) => s.demoPickerOpen);
  const setDemoPickerOpen = useAppStore((s) => s.setDemoPickerOpen);
  const loadDemoCourses = useAppStore((s) => s.loadDemoCourses);
  const loadSettings = useAppStore((s) => s.loadSettings);
  const { lock, isEnabled, enableEncryption } = useAppLock();
  const { theme, toggleTheme } = useTheme();

  const pickerRef = useRef<HTMLDivElement>(null);

  const handleLoadDataset = useCallback(
    (index: number) => {
      const { courses, settings } = hydrateDemoDataset(index);
      loadDemoCourses(courses);
      loadSettings(settings);
      recalculateStore();
      setDemoPickerOpen(false);
    },
    [loadDemoCourses, loadSettings, setDemoPickerOpen],
  );

  // Close the dropdown when clicking outside or pressing Escape.
  useEffect(() => {
    if (!demoPickerOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setDemoPickerOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDemoPickerOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [demoPickerOpen, setDemoPickerOpen]);

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-surface px-4 py-3 sm:px-6">
      <div className="mx-auto flex max-w-6xl items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary-hover text-white shadow-sm">
            <CapIcon className="h-5 w-5" aria-hidden="true" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-[var(--color-text)]">GPA Scales</h1>
            <p className="hidden text-xs text-[var(--color-text-muted)] sm:block">
              UC &amp; Common App Weighted GPA
            </p>
            <p className="text-xs text-[var(--color-text-muted)] sm:hidden">
              GPA Calculator
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <ProfileSwitcher />

          {/* Load Demo dataset picker — header nav link */}
          {showLoadDemo && (
            <div className="relative" ref={pickerRef}>
              <button
                onClick={() => setDemoPickerOpen(!demoPickerOpen)}
                className="rounded-md border border-border bg-surface px-3 py-1.5 text-sm font-medium text-[var(--color-text-secondary)] transition-[var(--transition-fast)] hover:border-primary hover:text-primary"
                aria-haspopup="menu"
                aria-expanded={demoPickerOpen}
                aria-label="Load demo dataset"
              >
                Load Demo
                <svg className="ml-1 inline h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {demoPickerOpen && (
                <div
                  role="menu"
                  className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-lg border border-border bg-surface shadow-lg"
                >
                  <p className="border-b border-border px-4 py-2 text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
                    Sample transcripts
                  </p>
                  <ul className="max-h-80 overflow-y-auto">
                    {DEMO_DATASETS.map((ds, i) => (
                      <li key={ds.name}>
                        <button
                          role="menuitem"
                          onClick={() => handleLoadDataset(i)}
                          className="block w-full px-4 py-3 text-left transition-[var(--transition-fast)] hover:bg-[var(--color-bg-tertiary)]"
                        >
                          <span className="block text-sm font-medium text-[var(--color-text)]">{ds.name}</span>
                          <span className="block text-xs text-[var(--color-text-muted)]">{ds.description}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {isEnabled && lock ? (
            <LockMenu lock={lock} />
          ) : (
            <button
              onClick={enableEncryption}
              className="rounded-md p-2 text-[var(--color-text-secondary)] transition-[var(--transition-fast)] hover:bg-[var(--color-bg-tertiary)]"
              aria-label="Enable password protection"
              title="Enable password protection"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </button>
          )}
          <button
            onClick={toggleTheme}
            className="rounded-md p-2 text-[var(--color-text-secondary)] transition-[var(--transition-fast)] hover:bg-[var(--color-bg-tertiary)]"
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
          >
            {theme === 'dark' ? (
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            ) : (
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            )}
          </button>
          <button
            onClick={toggleSettings}
            className={`rounded-md p-2 transition-[var(--transition-fast)] ${
              settingsOpen
                ? 'bg-primary-light text-primary'
                : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]'
            }`}
            aria-label="Toggle settings"
            aria-expanded={settingsOpen}
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </button>
        </div>
      </div>
    </header>
  );
}
